import { ConfigSlots, isBlankConfigValue, parseConfigCommand } from "./configCommands";

// Result returned when a prerequisite or selected-override expression is evaluated.
type EvaluationResult = {
  value: boolean;
  isValidExpression: boolean;
  errors: string[];
};

// Removes outer parentheses only when they wrap the entire expression.
function stripWrappingParentheses(expression: string) {
  let nextExpression = expression.trim();

  while (nextExpression.startsWith("(") && nextExpression.endsWith(")")) {
    let depth = 0;
    let wrapsWholeExpression = true;

    for (let index = 0; index < nextExpression.length; index += 1) {
      const character = nextExpression[index];

      if (character === "(") {
        depth += 1;
      } else if (character === ")") {
        depth -= 1;
      }

      if (depth === 0 && index < nextExpression.length - 1) {
        wrapsWholeExpression = false;
        break;
      }

      if (depth < 0) {
        return nextExpression;
      }
    }

    if (!wrapsWholeExpression) {
      break;
    }

    nextExpression = nextExpression.slice(1, -1).trim();
  }

  return nextExpression;
}

// Finds an AND/OR operator that is not nested inside parentheses.
function findTopLevelOperator(expression: string, operator: "&&" | "||") {
  let depth = 0;

  for (let index = 0; index < expression.length - 1; index += 1) {
    const character = expression[index];

    if (character === "(") {
      depth += 1;
      continue;
    }

    if (character === ")") {
      depth -= 1;
      continue;
    }

    if (depth === 0 && expression.slice(index, index + 2) === operator) {
      return index;
    }
  }

  return -1;
}

// Checks that an expression has matching opening and closing parentheses.
function hasBalancedParentheses(expression: string) {
  let depth = 0;

  for (const character of expression) {
    if (character === "(") {
      depth += 1;
    } else if (character === ")") {
      depth -= 1;
    }

    if (depth < 0) {
      return false;
    }
  }

  return depth === 0;
}

// Evaluates one leaf expression, such as "Variant Model:21C" or "true".
function evaluateSingle(expression: string, slots: ConfigSlots): EvaluationResult {
  const trimmedExpression = expression.trim();
  const normalizedExpression = trimmedExpression.toLowerCase();

  if (
    isBlankConfigValue(trimmedExpression) ||
    normalizedExpression === "true" ||
    normalizedExpression === "t" ||
    normalizedExpression === "yes" ||
    normalizedExpression === "y"
  ) {
    return { value: true, isValidExpression: true, errors: [] };
  }

  if (
    normalizedExpression === "false" ||
    normalizedExpression === "f" ||
    normalizedExpression === "no" ||
    normalizedExpression === "n"
  ) {
    return { value: false, isValidExpression: true, errors: [] };
  }

  const command = parseConfigCommand(trimmedExpression);

  if (!command) {
    return {
      value: false,
      isValidExpression: false,
      errors: [trimmedExpression],
    };
  }

  const directedSlot = command.slot.startsWith("*")
    ? slots[command.slot.slice(1)] || command.slot
    : command.slot;
  const hasSlot = Object.prototype.hasOwnProperty.call(slots, directedSlot);

  return {
    value: hasSlot && slots[directedSlot] === command.value,
    isValidExpression: hasSlot,
    errors: hasSlot ? [] : [trimmedExpression],
  };
}

// Evaluates full CSV expressions with ||, &&, !, parentheses, and Slot:Value checks.
export function evaluateConfigExpression(
  expression: string | undefined,
  slots: ConfigSlots,
): EvaluationResult {
  const trimmedExpression = stripWrappingParentheses((expression || "").trim());

  if (isBlankConfigValue(trimmedExpression)) {
    return { value: true, isValidExpression: true, errors: [] };
  }

  const orIndex = findTopLevelOperator(trimmedExpression, "||");
  if (orIndex >= 0) {
    const left = evaluateConfigExpression(trimmedExpression.slice(0, orIndex), slots);
    const right = evaluateConfigExpression(trimmedExpression.slice(orIndex + 2), slots);

    return {
      value: left.value || right.value,
      isValidExpression: left.isValidExpression && right.isValidExpression,
      errors: [...left.errors, ...right.errors],
    };
  }

  const andIndex = findTopLevelOperator(trimmedExpression, "&&");
  if (andIndex >= 0) {
    const left = evaluateConfigExpression(trimmedExpression.slice(0, andIndex), slots);
    const right = evaluateConfigExpression(trimmedExpression.slice(andIndex + 2), slots);

    return {
      value: left.value && right.value,
      isValidExpression: left.isValidExpression && right.isValidExpression,
      errors: [...left.errors, ...right.errors],
    };
  }

  if (trimmedExpression.startsWith("!")) {
    const inner = evaluateConfigExpression(trimmedExpression.slice(1), slots);
    return {
      value: !inner.value,
      isValidExpression: inner.isValidExpression,
      errors: inner.errors,
    };
  }

  if (!hasBalancedParentheses(trimmedExpression)) {
    return {
      value: false,
      isValidExpression: false,
      errors: [trimmedExpression],
    };
  }

  return evaluateSingle(trimmedExpression, slots);
}
